import type { DarkSettings, LookScheme } from "@/types/look-system";
import { isLookSystem } from "@/utils/look-gate";
import { appearanceCookieName } from "@/utils/website-appearance";

export type LookSchemeMode = NonNullable<DarkSettings["mode"]>;
export type LookSwitchPlacement = "header" | "menu" | "footer";

export interface LookSchemeSettings {
  mode: LookSchemeMode;
  start: LookScheme | "device";
  base: LookScheme;
  /** Where visitors find the switch; empty unless visitors choose. */
  placements: LookSwitchPlacement[];
  /** Set only when the dark scheme can be shown. */
  logo?: string;
}

const MODES: readonly LookSchemeMode[] = ["light", "dark", "device", "visitor"];
const PLACEMENTS: readonly LookSwitchPlacement[] = ["header", "menu", "footer"];

const isScheme = (value: unknown): value is LookScheme =>
  value === "light" || value === "dark";

/**
 * `colorSystem.dark` with its defaults, or undefined for a website not on the look system. Without a mode the website
 * is always its `base` scheme; visitors start in `base` and find the switch in the header unless `switch` says otherwise.
 */
export function lookSchemeSettings(
  theme: unknown,
): LookSchemeSettings | undefined {
  if (!isLookSystem(theme)) return undefined;
  const base: LookScheme = theme.colorSystem.base === "dark" ? "dark" : "light";
  const dark: Partial<Record<keyof DarkSettings, unknown>> =
    theme.colorSystem.dark && typeof theme.colorSystem.dark === "object"
      ? theme.colorSystem.dark
      : {};
  const mode = MODES.find((value) => value === dark.mode) ?? base;
  const start =
    isScheme(dark.start) || dark.start === "device" ? dark.start : base;
  const chosen: unknown[] | undefined = Array.isArray(dark.switch)
    ? dark.switch
    : undefined;
  const placements =
    mode !== "visitor"
      ? []
      : chosen
        ? PLACEMENTS.filter((placement) => chosen.includes(placement))
        : (["header"] as LookSwitchPlacement[]);
  const settings: LookSchemeSettings = { mode, start, base, placements };
  if (mode !== "light" && typeof dark.logo === "string" && dark.logo.trim())
    settings.logo = dark.logo;
  return settings;
}

/**
 * The scheme the server renders: the forced one, else the visitor's saved choice, else the start. Undefined while the
 * device decides; `lookSchemeScript` settles it before the first paint.
 */
export function resolveLookScheme(
  settings: LookSchemeSettings,
  cookie?: string,
): LookScheme | undefined {
  if (isScheme(settings.mode)) return settings.mode;
  if (settings.mode === "device") return undefined;
  let saved: unknown;
  try {
    saved = JSON.parse(decodeURIComponent(cookie ?? "{}"))?.scheme;
  } catch {
    saved = undefined;
  }
  if (isScheme(saved)) return saved;
  return settings.start === "device" ? undefined : settings.start;
}

/** The inline script that sets the device's scheme before paint, or '' when the server already knows the scheme. */
export function lookSchemeScript(
  settings: LookSchemeSettings,
  scheme: LookScheme | undefined,
): string {
  if (scheme) return "";
  const follow =
    settings.mode === "device" ? 'query.addEventListener("change",apply);' : "";
  return `(()=>{try{const query=matchMedia("(prefers-color-scheme: dark)"),apply=()=>{document.documentElement.dataset.reScheme=query.matches?"dark":"light"};apply();${follow}}catch(error){}})()`;
}

export interface LookSwitchProps {
  placement: LookSwitchPlacement;
  cookieName: string;
}

/** The switch's props where visitors choose the scheme at `placement`, else null. */
export function lookSwitchProps(
  website: { theme?: unknown; applicationId?: string } | null | undefined,
  placement: LookSwitchPlacement,
): LookSwitchProps | null {
  return lookSchemeSettings(website?.theme)?.placements.includes(placement)
    ? { placement, cookieName: appearanceCookieName(website?.applicationId) }
    : null;
}

/** The scheme of a page no visitor or device has chosen for, such as a CMS preview; undefined off the look system. */
export function lookStartScheme(theme: unknown): LookScheme | undefined {
  const settings = lookSchemeSettings(theme);
  return settings && (resolveLookScheme(settings) ?? settings.base);
}
